import {
  Badge,
  Box,
  Button,
  Flex,
  Grid,
  Group,
  Paper,
  Text,
  TextInput,
  PasswordInput,
  ThemeIcon,
  useMantineTheme
} from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';
import React, { useEffect, useState } from 'react';
import { api } from '../../services/master.service';
import { showErrorToast, showSuccessToast } from '../../utilities/Toast';
import ViewOrderModal from '../../components/ViewOrderModal';
import moment from 'moment';
import Joi from 'joi';
import { joiResolver, useForm } from '@mantine/form';
import { is_mobile_view } from '../../config';
import {
  IconCalendarEvent,
  IconEdit,
  IconLock,
  IconMail,
  IconMapPin,
  IconPhone,
  IconShieldCheck,
  IconUser
} from '@tabler/icons-react';

function DetailCard({ icon: Icon, label, value, helper, color = '#0d7a2a' }) {
  return (
    <Paper
      radius={18}
      p={18}
      withBorder
      sx={{
        minHeight: 108,
        position: 'relative',
        overflow: 'hidden',
        background: 'linear-gradient(145deg, rgba(255,255,255,.99), rgba(248,250,252,.94))',
        borderColor: 'rgba(34, 197, 94, 0.16)',
        boxShadow: '0 12px 30px rgba(15, 23, 42, 0.05)',
      }}
    >
      <Box
        sx={{
          position: 'absolute',
          right: -18,
          top: -28,
          width: 72,
          height: 72,
          borderRadius: '50%',
          background: `${color}14`,
        }}
      />
      <Group spacing={14} noWrap sx={{ position: 'relative', zIndex: 1 }}>
        <ThemeIcon size={38} radius={12} variant="light" sx={{ color, background: `${color}12` }}>
          <Icon size={19} />
        </ThemeIcon>
        <Box>
          <Text size={10} fw={900} tt="uppercase" color="dimmed">
            {label}
          </Text>
          <Text size={15} fw={900} color="dark" mt={5} lh={1.25}>
            {value || 'Not available'}
          </Text>
          <Text size={12} fw={800} mt={4} sx={{ color }}>
            {helper}
          </Text>
        </Box>
      </Group>
    </Paper>
  );
}

function AccountDetails() {
  let isMobileScreen = useMediaQuery('(max-width: 768px)');
  isMobileScreen = is_mobile_view ? is_mobile_view : isMobileScreen;
  const theme = useMantineTheme();

  const [user, setUser] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [isChangePwd, setIsChangePwd] = useState(false);
  const [isEditingProfile, setIsEditingProfile] = useState(false);

  useEffect(() => {
    const user = JSON.parse(localStorage.getItem('user'));
    setUser(user);
    form.setValues({
      first_name: user.first_name,
      email: user.email,
      username: user.username,
    });
  }, []);

  const schema = Joi.object({
    first_name: Joi.string().required(),
    email: Joi.string().required(),
    username: Joi.string().required(),
  });

  const schema2 = Joi.object({
    old_password: Joi.string().required(),
    new_password: Joi.string().min(3).max(15).required().label('Password'),
    confirm_password: Joi.any()
      .equal(Joi.ref('new_password'))
      .required()
      .label('Confirm password')
      .messages({ 'any.only': '{{#label}} does not match' }),
  });

  const form = useForm({
    initialValues: {
      first_name: '',
      email: '',
      username: '',
    },
    validate: joiResolver(schema),
  });

  const passwordForm = useForm({
    initialValues: {
      old_password: '',
      new_password: '',
      confirm_password: '',
    },
    validate: joiResolver(schema2),
  });

const editDetails = async (values) => {
    setIsLoading(true); 

    await api.edit_user(values).then((res) => {
      setIsLoading(false); 
      console.log(res.data);
      
      if (res.data.success) {
        
        const updatedUser = res.data.user; 
        
        setUser(updatedUser); 
        
        localStorage.setItem('user_first_name', updatedUser.first_name); 
        
        localStorage.setItem('user', JSON.stringify(updatedUser)); 

        showSuccessToast({
          title: 'Success',
          message: 'Successfully edited details.',
        });
        
      } else {
        showErrorToast({
          title: 'Error',
          message: res.data.message || 'Error editing details.', 
        });
      }
    }).catch(error => {
        setIsLoading(false);
        showErrorToast({
            title: 'Error',
            message: 'Network error or unhandled exception.',
        });
    });
};

  const changePassword = async (values) => {
    await api
      .change_password({
        username: user.username,
        old_password: values.old_password,
        new_password: values.new_password,
      })
      .then((res) => {
        console.log(res.data);
        if (res.data.success) {
          showSuccessToast({
            title: 'Success',
            message: 'Successfully changed password.',
          });
          passwordForm.reset({});
        } else {
          showErrorToast({
            title: 'Error',
            message: res.data.message,
          });
        }
      });
  };

  const profileName = user?.first_name || localStorage.getItem('user_first_name') || localStorage.getItem('user_name') || 'User';
  const mobileNumber = user?.username || localStorage.getItem('user_name') || '';
  const email = user?.email || '';
  const location = user?.city || localStorage.getItem('city_name') || '';
  const joinedDate = user?.date_joined || user?.created_at || user?.created || '';

  return (
    <Box>
      {(isChangePwd || isEditingProfile) && (
        <Paper
          radius={22}
          p={isMobileScreen ? 16 : 24}
          mb={20}
          sx={{
            position: 'relative',
            overflow: 'hidden',
            background: `linear-gradient(135deg, ${theme.colors.green[0]}, ${theme.white} 56%, ${theme.colors.gray[0]})`,
            border: `1px solid ${theme.colors.green[1]}`,
            boxShadow: '0 18px 42px rgba(13, 71, 21, 0.08)',
          }}
        >
          <Box
            sx={{
              position: 'absolute',
              right: -46,
              top: -72,
              width: 180,
              height: 180,
              borderRadius: '50%',
              background: 'radial-gradient(circle, rgba(34,197,94,.18), transparent 68%)',
            }}
          />
          <Flex
            justify="space-between"
            align={isMobileScreen ? 'flex-start' : 'center'}
            direction={isMobileScreen ? 'column' : 'row'}
            gap={14}
            sx={{ position: 'relative', zIndex: 1 }}
          >
            <Group spacing={12} noWrap={!isMobileScreen}>
              <ThemeIcon
                size={54}
                radius={16}
                sx={{
                  background: `linear-gradient(135deg, ${theme.colors.brand[7]}, ${theme.colors.brand[5]})`,
                  boxShadow: '0 12px 24px rgba(13, 71, 21, 0.18)',
                }}
              >
                {isChangePwd ? <IconLock size={27} /> : <IconUser size={27} />}
              </ThemeIcon>
              <Box>
                <Text fw={900} size={isMobileScreen ? 22 : 26} color="dark" lh={1.1}>
                  {isChangePwd ? 'Change Password' : 'Edit Profile'}
                </Text>
                <Text size="sm" color="dimmed" mt={5}>
                  {isChangePwd ? 'Update your login password securely.' : 'Keep your personal details accurate and up to date.'}
                </Text>
              </Box>
            </Group>

            {isChangePwd ? (
              <Button
                radius="xl"
                variant="outline"
                fullWidth={isMobileScreen}
                onClick={() => {
                  setIsChangePwd(false);
                  setIsEditingProfile(false);
                }}
                sx={{
                  height: 42,
                  paddingInline: 22,
                  fontWeight: 800,
                  borderColor: theme.colors.green[3],
                  color: theme.colors.brand[7],
                  background: 'rgba(255,255,255,0.74)',
                }}
              >
                Cancel
              </Button>
            ) : null}
          </Flex>
        </Paper>
      )}

      {!isChangePwd && !isEditingProfile && (
        <>
          <Paper
            radius={20}
            p={isMobileScreen ? 14 : 16}
            mb={16}
            sx={{
              position: 'relative',
              overflow: 'hidden',
              background: `linear-gradient(135deg, ${theme.colors.green[0]}, ${theme.white} 48%, rgba(220,252,231,.72))`,
              border: `1px solid ${theme.colors.green[1]}`,
              boxShadow: '0 18px 42px rgba(13, 71, 21, 0.08)',
            }}
          >
            <Flex
              justify="space-between"
              align={isMobileScreen ? 'flex-start' : 'center'}
              direction={isMobileScreen ? 'column' : 'row'}
              gap={12}
              sx={{ position: 'relative', zIndex: 1 }}
            >
              <Group spacing={12} noWrap={!isMobileScreen}>
                <Box sx={{ position: 'relative', flex: '0 0 auto' }}>
                  <Flex
                    align="center"
                    justify="center"
                    sx={{
                      width: isMobileScreen ? 58 : 66,
                      height: isMobileScreen ? 58 : 66,
                      borderRadius: '50%',
                      color: '#ffffff',
                      fontSize: isMobileScreen ? 26 : 30,
                      fontWeight: 900,
                      background: `linear-gradient(145deg, ${theme.colors.brand[6]}, ${theme.colors.brand[8]})`,
                      boxShadow: '0 16px 30px rgba(13, 122, 42, .22)',
                    }}
                  >
                    {profileName.charAt(0).toUpperCase()}
                  </Flex>
                  <Box
                    sx={{
                      position: 'absolute',
                      right: 4,
                      bottom: 4,
                      width: 14,
                      height: 14,
                      borderRadius: '50%',
                      background: '#22c55e',
                      border: '3px solid #ffffff',
                    }}
                  />
                </Box>

                <Box>
                  <Text fw={900} size={isMobileScreen ? 22 : 25} color="dark" lh={1.05}>
                    {profileName}
                  </Text>
                  <Group spacing={7} mt={6}>
                    <IconPhone size={15} color={theme.colors.gray[6]} />
                    <Text size="sm" color="dimmed" fw={700}>
                      {mobileNumber || 'Mobile not added'}
                    </Text>
                  </Group>
                  <Badge
                    mt={8}
                    radius="xl"
                    variant="light"
                    color="green"
                    leftSection={<IconShieldCheck size={13} />}
                    sx={{ fontWeight: 900 }}
                  >
                    Account Active
                  </Badge>
                </Box>
              </Group>

              <Group spacing={10} w={isMobileScreen ? '100%' : 'auto'}>
                <Button
                  radius="xl"
                  leftIcon={<IconLock size={15} />}
                  fullWidth={isMobileScreen}
                  onClick={() => {
                    setIsChangePwd(true);
                    setIsEditingProfile(false);
                  }}
                  sx={{
                    height: 38,
                    fontWeight: 850,
                    background: `linear-gradient(135deg, ${theme.colors.brand[7]}, ${theme.colors.brand[5]})`,
                    boxShadow: '0 14px 28px rgba(13, 71, 21, 0.18)',
                    '&:hover': {
                      transform: 'translateY(-2px)',
                      boxShadow: '0 18px 36px rgba(13, 71, 21, 0.24)',
                    },
                  }}
                >
                  Change Password
                </Button>
                <Button
                  radius="xl"
                  leftIcon={<IconEdit size={15} />}
                  fullWidth={isMobileScreen}
                  onClick={() => {
                    setIsEditingProfile(true);
                    setIsChangePwd(false);
                  }}
                  sx={{
                    height: 38,
                    fontWeight: 850,
                    background: `linear-gradient(135deg, ${theme.colors.brand[7]}, ${theme.colors.brand[5]})`,
                    boxShadow: '0 14px 28px rgba(13, 71, 21, 0.18)',
                  }}
                >
                  Edit Profile
                </Button>
              </Group>
            </Flex>
          </Paper>

          <Grid gutter={16}>
            <Grid.Col span={12} md={6}>
              <DetailCard icon={IconMail} label="Email Address" value={email} helper={email ? 'Verified' : 'Add Email'} color="#0d7a2a" />
            </Grid.Col>
            <Grid.Col span={12} md={6}>
              <DetailCard icon={IconPhone} label="Mobile Number" value={mobileNumber} helper={mobileNumber ? 'Primary Login' : 'Add Number'} color="#1677ff" />
            </Grid.Col>
            <Grid.Col span={12} md={6}>
              <DetailCard
                icon={IconCalendarEvent}
                label="Joined On"
                value={joinedDate ? new Date(joinedDate).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : 'Not available'}
                helper="Account timeline"
                color="#7c3aed"
              />
            </Grid.Col>
            <Grid.Col span={12} md={6}>
              <DetailCard icon={IconMapPin} label="Location" value={location || 'Not available'} helper={location ? 'Delivery area' : 'Add Location'} color="#f59f00" />
            </Grid.Col>
          </Grid>
        </>
      )}

      {!isChangePwd && isEditingProfile && (
        <Paper
          radius={22}
          p={isMobileScreen ? 16 : 24}
          withBorder
          sx={{
            overflow: 'hidden',
            background: 'linear-gradient(145deg, rgba(255,255,255,.99), rgba(248,250,252,.95))',
            borderColor: theme.colors.gray[2],
            boxShadow: '0 14px 34px rgba(15, 23, 42, 0.06)',
          }}
        >
          <Flex align="center" justify="space-between" gap={12} mb={22} wrap="wrap">
            <Group spacing={12} noWrap>
              <ThemeIcon size={46} radius="xl" color="green" variant="light">
                <IconEdit size={23} />
              </ThemeIcon>
              <Box>
                <Text fw={900} size="lg" color="dark">
                  Edit Profile
                </Text>
                <Text size="sm" color="dimmed" mt={3}>
                  Update your account details.
                </Text>
              </Box>
            </Group>
            <Button
              radius="xl"
              variant="outline"
              onClick={() => {
                setIsEditingProfile(false);
              }}
            >
              Cancel
            </Button>
          </Flex>

          <form
            onSubmit={form.onSubmit((values) => {
              editDetails(values);
            })}
          >
            <Grid gutter={16}>
              <Grid.Col span={12}>
                <TextInput
                  icon={<IconUser size={17} />}
                  radius="md"
                  placeholder="Enter First Name"
                  label="First Name"
                  {...form.getInputProps('first_name')}
                />
              </Grid.Col>
              <Grid.Col span={12} md={6} lg={6}>
                <TextInput
                  icon={<IconMail size={17} />}
                  radius="md"
                  placeholder="Enter Email"
                  label="Email address"
                  {...form.getInputProps('email')}
                />
              </Grid.Col>
              <Grid.Col span={12} md={6} lg={6}>
                <TextInput
                  icon={<IconPhone size={17} />}
                  radius="md"
                  disabled
                  placeholder="Enter Username"
                  label="Username"
                  {...form.getInputProps('username')}
                />
              </Grid.Col>
            </Grid>
            <Button
              mt={22}
              radius="xl"
              w={'100%'}
              type="submit"
              loading={isLoading}
              sx={{
                height: 44,
                fontWeight: 900,
                background: `linear-gradient(135deg, ${theme.colors.brand[7]}, ${theme.colors.brand[5]})`,
                boxShadow: '0 12px 24px rgba(13, 71, 21, 0.16)',
              }}
            >
              Edit Account Details
            </Button>
          </form>
        </Paper>
      )}

      {isChangePwd ? (
        <Paper
          radius={22}
          p={isMobileScreen ? 16 : 24}
          withBorder
          sx={{
            overflow: 'hidden',
            background: 'linear-gradient(145deg, rgba(255,255,255,.99), rgba(248,250,252,.95))',
            borderColor: theme.colors.gray[2],
            boxShadow: '0 14px 34px rgba(15, 23, 42, 0.06)',
          }}
        >
          <Group spacing={12} mb={22} noWrap>
            <ThemeIcon size={46} radius="xl" color="green" variant="light">
              <IconLock size={23} />
            </ThemeIcon>
            <Box>
              <Text fw={900} size="lg" color="dark">
                Password Security
              </Text>
              <Text size="sm" color="dimmed" mt={3}>
                Choose a strong password between 3 and 15 characters.
              </Text>
            </Box>
          </Group>

          <form
            onSubmit={passwordForm.onSubmit((values) => {
              changePassword(values);
            })}
          >
            <Grid gutter={16}>
              <Grid.Col span={12}>
                <PasswordInput
                  icon={<IconLock size={17} />}
                  radius="md"
                  placeholder="Enter Old password"
                  label="Old password"
                  // type="password"
                  {...passwordForm.getInputProps('old_password')}
                />
              </Grid.Col>
              <Grid.Col span={12} md={6} lg={6}>
                <PasswordInput
                  icon={<IconLock size={17} />}
                  radius="md"
                  placeholder="Enter New Password"
                  label="New password"
                  // type="password"
                  {...passwordForm.getInputProps('new_password')}
                />
              </Grid.Col>
              <Grid.Col span={12} md={6} lg={6}>
                <PasswordInput
                  icon={<IconShieldCheck size={17} />}
                  radius="md"
                  placeholder="Enter Confirm Password"
                  label="Confirm Password"
                  // type="password"
                  {...passwordForm.getInputProps('confirm_password')}
                />
              </Grid.Col>
            </Grid>
            <Button
              mt={22}
              radius="xl"
              w={'100%'}
              type="submit"
              sx={{
                height: 44,
                fontWeight: 900,
                background: `linear-gradient(135deg, ${theme.colors.brand[7]}, ${theme.colors.brand[5]})`,
                boxShadow: '0 12px 24px rgba(13, 71, 21, 0.16)',
              }}
            >
              Change Password
            </Button>
          </form>
        </Paper>
      ) : (
        <></>
      )}
    </Box>
  );
}

export default AccountDetails;
